/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import type { ToggleModalProps } from '../../../../../../components/modal/types';
import type { Tag as TagType } from '@whizz/api-model';
import type { AxiosError } from 'axios';
import { useI18n } from '@whizz/react-i18n';
import { Input } from '../../../../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../../../../global';
import { QUERIES_IDS } from '../../../../../../util/vars';
import { ColorPicker } from '../../../../modules/ColorPicker';
import { getDefaultTagColor } from '../../../../../../util/styled';
import { repository } from '@whizz/api-client';
import { useMutation, useQueryClient } from 'react-query';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { ModalFooter } from '../../../../../../components/modal/Modal';

const MIN_LABEL_WIDTH = 150;

const AddEditTagModal = ({ data, toggle, tagCount = 1, onSuccess, onError }: ModalProps): JSX.Element => {
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const editMode = null != data;

  const mutations = {
    create: useMutation(
      ({ formData }: { formData: FormData }) => repository.TagsRepository.addTag(formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ]);
          onSuccess?.();
          toggle(false);
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
        },
      },
    ),
    update: useMutation(
      ({ id, formData }: { id: number, formData: FormData }) => repository.TagsRepository.updateTag(id, formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ]);
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS ]);
          onSuccess?.();
          toggle(false);
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
        },
      },
    ),
  };

  const handleSubmit = (formData: FormData) => {
    if (!editMode) {
      mutations.create.mutate({ formData });
    } else {
      mutations.update.mutate({ id: data!.id, formData });
    }
  };

  const VALIDATION_SCHEMA = Yup.object().shape({
    abbreviation: Yup.string()
      .required(translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.VALIDATIONS.ABBREVIATION.REQUIRED'))
      .max(5, translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.VALIDATIONS.ABBREVIATION.MAX')),
    description: Yup.string()
      .required(translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.VALIDATIONS.DESCRIPTION.REQUIRED')),
  });

  return (
    <Formik<FormData>
      initialValues={{
        abbreviation: '',
        description: '',
        color: getDefaultTagColor(tagCount),
        // Editing
        ...(editMode ? data : {}),
      }}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={(formData) => handleSubmit(formData)}
    >
      {({ handleSubmit, getFieldProps, setFieldValue, resetForm, errors, touched, isValid, values }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container space-y-6'}>
            <Input
              {...getFieldProps('description')}
              id={'description'}
              label={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DESCRIPTION')}
              labelStyle={'bold'}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.description ? errors.description : null}
              required
            />
            <Input
              {...getFieldProps('abbreviation')}
              id={'abbreviation'}
              label={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.ABBREVIATION')}
              labelStyle={'bold'}
              hint={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.HINTS.ABBREVIATION')}
              maxLength={5}
              minWidthForLabel={MIN_LABEL_WIDTH}
              error={touched.abbreviation ? errors.abbreviation : null}
              required
            />

            {(!editMode || !data?.isDefault) && (
              <>
                <Field type={'hidden'} value={values.color} name={'color'} />
                <ColorPicker
                  title={translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.LABEL_COLOR')}
                  label={values.abbreviation}
                  value={values.color}
                  onChange={(value ) => setFieldValue('color', value )}
                />
              </>
            )}

            {(!isValid && Object.keys(errors).some((key) => touched[key as keyof FormData]))
            && (<FormGenericError />)}
          </div>

          <ModalFooter justify={editMode ? 'between' : 'end'}>
            {editMode && (
              <CancelButtonModal
                align={'start'}
                onClick={() => resetForm()}
              >
                {translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.BUTTONS.DISCARD')}
              </CancelButtonModal>
            )}
            <SaveButton
              type={'submit'}
              isLoading={mutations.create.isLoading || mutations.update.isLoading}
            />
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

export default AddEditTagModal;


export interface ModalProps extends Pick<ToggleModalProps, 'toggle'> {
  data?: TagType;
  tagCount: number;
  onSuccess?: () => void;
  onError?: () => void;
}

interface FormData {
  abbreviation: string;
  description: string;
  color: number;
}
